import { useEffect, useState } from "react";
import Icon from "components/Icons/Icon";
import useAppStoreLink from "components/common/useAppStoreLink";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import BookingShot from "img/app-booking.webp";
import BudgetShot from "img/app-budget.webp";
import DayPlanShot from "img/app-dayPlan.webp";
import EsimShot from "img/app-esim.webp";
import PackingShot from "img/app-packing.webp";
import RouteShot from "img/app-route.webp";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { getHomeTranslations } from "../marketing/i18n/pages/home";

// Same brand "S" mark used by BlogBanner.tsx's S_MARK_PATH / HeroShowcase.tsx's S_PATH.
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";
const S_VB = { w: 81, h: 108 };

type ToolboxItemMeta = {
	icon: React.ComponentProps<typeof Icon>["name"];
	key: string;
	name: string;
	desc: string;
	color: string;
	lightBg: string;
	tilt: number;
};

const TOOLBOX_FEATURES: ToolboxItemMeta[] = [
	{
		icon: "map--chunky",
		key: "route",
		name: "Route planning",
		desc: "Map every stop, auto-calculate distances and travel times.",
		color: "#2e9e72",
		lightBg: "#e8f9f4",
		tilt: -7,
	},
	{
		icon: "calendar--chunky",
		key: "dayPlan",
		name: "Day-by-day plan",
		desc: "Your day-by-day holiday itinerary: times, places and notes in one timeline.",
		color: "#2e9e72",
		lightBg: "#e8f9f4",
		tilt: 5,
	},
	{
		icon: "bed--chunky",
		key: "booking",
		name: "Booking",
		desc: "Search and save hotels, Airbnbs and hostels in one place.",
		color: "#2e9e72",
		lightBg: "#e8f9f4",
		tilt: -5,
	},
	{
		icon: "money-bag--chunky",
		key: "budget",
		name: "Budget tracker",
		desc: "Track every expense and split costs with your travel crew.",
		color: "#f5a623",
		lightBg: "#fff8ec",
		tilt: 6,
	},
	{
		icon: "backpack--chunky",
		key: "packing",
		name: "Packing list",
		desc: "Never forget a charger again, smart lists that travel with you.",
		color: "#e84d7e",
		lightBg: "#fff0f5",
		tilt: -4,
	},
	{
		icon: "wifi--chunky",
		key: "esim",
		name: "eSIM",
		desc: "Buy and activate a local data plan for 190+ countries, right in the app.",
		color: "#e84d7e",
		lightBg: "#fff0f5",
		tilt: 4,
	},
	{
		icon: "documents--chunky",
		key: "documents",
		name: "Documents",
		desc: "Store boarding passes, visas and vouchers, attached to the right day.",
		color: "#f5a623",
		lightBg: "#fff8ec",
		tilt: -6,
	},
	{
		icon: "photo--chunky",
		key: "journal",
		name: "Journal",
		desc: "Capture memories as you go: photos, notes, moments.",
		color: "#7c3aed",
		lightBg: "#f5f0ff",
		tilt: 7,
	},
	{
		icon: "video-camera--chunky",
		key: "reels",
		name: "Trip Reels",
		desc: "Turn your trip into a shareable video reel in seconds.",
		color: "#e84d7e",
		lightBg: "#fff0f5",
		tilt: -5,
	},
	{
		icon: "compass--chunky",
		key: "discover",
		name: "Discover",
		desc: "Find inspiration from real trips other travelers took.",
		color: "#2e9e72",
		lightBg: "#e8f9f4",
		tilt: 6,
	},
	{
		icon: "sparkle--chunky",
		key: "aiPlanning",
		name: "AI planning",
		desc: "Just describe your trip: our AI builds the full itinerary, day by day.",
		color: "#7c3aed",
		lightBg: "#f5f0ff",
		tilt: -4,
	},
	{
		icon: "desktop--chunky",
		key: "desktop",
		name: "Desktop",
		desc: "Plan your trip on any device: full web app, no install needed.",
		color: "#2e9e72",
		lightBg: "#e8f9f4",
		tilt: 6,
	},
];

type ClusterTile = {
	icon: React.ComponentProps<typeof Icon>["name"];
	bg: string;
	color: string;
	tilt: number;
	left: number;
	top: number;
};

// Tilted app-icon cluster above the heading — same icons/positions as the flying
// tiles in the marketing redesign's FeatureToolboxSection hero.
const CLUSTER_TILES: ClusterTile[] = [
	{ icon: "compass--chunky", bg: "#e8f9f4", color: "#2e9e72", tilt: 8, left: 0, top: 10 },
	{ icon: "calendar--chunky", bg: "#fff8ec", color: "#f5a623", tilt: -11, left: 52, top: -4 },
	{ icon: "bed--chunky", bg: "#fff0f5", color: "#e84d7e", tilt: 7, left: 180, top: 4 },
	{ icon: "sparkle--chunky", bg: "#f5f0ff", color: "#7c3aed", tilt: -8, left: 218, top: 52 },
	{ icon: "video-camera--chunky", bg: "#e8f9f4", color: "#2e9e72", tilt: 6, left: 24, top: 66 },
	{ icon: "photo--chunky", bg: "#fff0f5", color: "#e84d7e", tilt: -7, left: 152, top: 72 },
];

type FanPhone = { rot: number; width: number; z: number; opacity: number; src: string; alt: string };

// Fan of app screenshots on the right — widths/rotations/z-order lifted straight
// from the Figma frame (ThreePhonesVisual), fanned from a shared bottom pivot.
//
// **Six real screens, one per phone.** Every phone rendered the same `trip-route-1.webp` before,
// so a fan built to show the breadth of the product showed one screen seven times. Ilse's
// shots (123yb6917mr) cover six of this section's eight features; `documents` and `journal`
// have no asset yet, which is why the seventh phone is gone rather than repeating one.
//
// Rotations rebalanced for six: dropping the last entry alone left three phones to the left of
// centre and two to the right, which reads as a mistake rather than a fan.
//
// Ordered so the front phone is the route — the product's signature screen, and what the single
// hero phone shows too.
const FAN_PHONES: FanPhone[] = [
	{ rot: -38, width: 236, z: 1, opacity: 0.88, src: EsimShot, alt: "" },
	{ rot: -23, width: 286, z: 3, opacity: 0.92, src: PackingShot, alt: "" },
	{ rot: -9, width: 312, z: 5, opacity: 0.96, src: BudgetShot, alt: "" },
	{ rot: 5, width: 348, z: 6, opacity: 1, src: RouteShot, alt: "The Stippl app showing a planned route through Japan" },
	{ rot: 19, width: 290, z: 4, opacity: 0.94, src: DayPlanShot, alt: "" },
	{ rot: 33, width: 240, z: 2, opacity: 0.88, src: BookingShot, alt: "" },
];

// Giant Stippl "S" clipped around a photo, tilted — decorative watermark that
// bleeds down past the section's own bottom edge into CommunitySection below
// (z-index escape, same technique as HeroShowcase's .intro-header__s-window;
// the left-edge fade into the section background is CSS mask-image on
// .feature-toolbox__s-photo, see Website.scss).
const SPhotoWindow = ({ photo, height, rotate = 0 }: { photo: string; height: number; rotate?: number }) => {
	const width = (height * S_VB.w) / S_VB.h;
	const cx = S_VB.w / 2;
	const cy = S_VB.h / 2;
	const shapeRotate = `rotate(${rotate} ${cx} ${cy})`;

	return (
		<svg width={width} height={height} viewBox={`0 0 ${S_VB.w} ${S_VB.h}`} style={{ display: "block", overflow: "visible" }} aria-hidden="true">
			<defs>
				<clipPath id="feature-toolbox-s-clip">
					<path d={S_PATH} transform={shapeRotate} />
				</clipPath>
			</defs>
			<g clipPath="url(#feature-toolbox-s-clip)">
				<image href={photo} x={0} y={0} width={S_VB.w} height={S_VB.h} preserveAspectRatio="xMidYMin slice" />
			</g>
		</svg>
	);
};

const FeatureToolboxSection = () => {
	// mounted-gate avoids an osName-driven hydration mismatch when this section
	// renders through the SSR marketing pipeline (see HomeMarketing.tsx) — "/register"
	// is a stable literal that matches on both server and the pre-hydration client pass.
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);

	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);

	const { appstoreUrl, handleTrack } = useAppStoreLink("marketing_site");
	const appDownloadHref = mounted ? appstoreUrl : "/register";
	// 123yb691qz1: opens the signup modal in place instead of navigating to /register;
	// `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store instead.
	const gate = usePlanGate("homepage_toolbox", "store");

	return (
		<>
		<section className="feature-toolbox">
			<div className="feature-toolbox__s-photo" aria-hidden="true">
				<SPhotoWindow photo="https://images.unsplash.com/photo-1506682834362-399453953e62?fit=crop&auto=format&q=80&w=900&h=1200" height={1600} rotate={12} />
			</div>

			<div className="feature-toolbox__inner">
				<div className="feature-toolbox__hero">
					<div className="feature-toolbox__intro">
						<div className="feature-toolbox__cluster" aria-hidden="true">
							<span className="feature-toolbox__cluster-main">
								<svg width="32" height="42" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="white" />
								</svg>
							</span>
							{CLUSTER_TILES.map((t, i) => (
								<span
									key={i}
									className="feature-toolbox__cluster-tile"
									style={{ left: t.left, top: t.top, background: t.bg, color: t.color, transform: `rotate(${t.tilt}deg)` }}
								>
									<Icon name={t.icon} size={22} fill={t.color} />
								</span>
							))}
						</div>

						<p className="sub-header">{t(translations, "home.toolbox.subHeader", "AI-powered travel planning")}</p>
						<h2>
							{t(translations, "home.toolbox.headingLine1", "Your AI travel planner.")}
							<br />
							<strong>
								{t(translations, "home.toolbox.headingStrongLine1", "Every tool")}
								<br />
								{t(translations, "home.toolbox.headingStrongLine2", "you need.")}
							</strong>
						</h2>
						<p>
							{t(
								translations,
								"home.toolbox.description",
								"Our AI turns a single prompt into a full itinerary: then you've got budget, packing lists, stays and bookings right alongside it. No spreadsheets, no chaos."
							)}
						</p>
						<div className="feature-toolbox__ctas">
							<a
								href="/register"
								className="btn-rounded solid xl"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "home.toolbox.ctaPrimary", "Start planning now →")}
							</a>
							<a href={appDownloadHref} className="btn-rounded outlined light xl" onClick={handleTrack} target="_blank" rel="noreferrer">
								<svg width="13" height="17" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "home.hero.ctaMobile", "Get the app")}
							</a>
						</div>
					</div>

					<div className="feature-toolbox__phones" aria-hidden="true">
						{FAN_PHONES.map((p, i) => (
							<div
								key={i}
								className="feature-toolbox__phone"
								style={{
									width: p.width,
									marginLeft: -p.width / 2,
									transform: `rotate(${p.rot}deg)`,
									zIndex: p.z,
									opacity: p.opacity,
									filter: `drop-shadow(0 ${p.z > 5 ? 36 : 16}px ${p.z > 5 ? 68 : 34}px rgba(0,0,0,${p.z > 5 ? 0.68 : 0.44}))`,
								}}
							>
								<img src={p.src} alt={p.alt} loading="lazy" />
							</div>
						))}
					</div>
				</div>

				<div className="feature-toolbox__grid">
					{TOOLBOX_FEATURES.map((f) => (
						<div className="toolbox-card" key={f.key}>
							<span
								className="toolbox-card__icon"
								style={{ background: f.lightBg, color: f.color, ["--tilt-deg" as string]: `${f.tilt}deg` } as React.CSSProperties}
							>
								<Icon name={f.icon} size={26} fill={f.color} />
							</span>
							<p className="toolbox-card__name">{t(translations, `home.toolbox.features.${f.key}.name`, f.name)}</p>
							<p className="toolbox-card__desc">{t(translations, `home.toolbox.features.${f.key}.desc`, f.desc)}</p>
						</div>
					))}
				</div>
			</div>
		</section>
		<PlanGateModal
			gate={gate}
			title={t(translations, "home.auth.startPlanning", "Create a free account to start planning")}
			loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
		/>
		</>
	);
};

export default FeatureToolboxSection;
